import { forwardRef, type RefObject, useMemo } from 'react'
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList
} from '@/components/ui/combobox'
import { ComboboxTrigger } from '@/features/marquee/components/combobox-trigger'
import { cn } from '@/helpers/utils'

export interface ComboboxOption {
	value: string
	label: string
}

interface ComboboxFieldProps {
	options: ComboboxOption[]
	value: string | null
	onChange: (value: string | null) => void
	placeholder: string
	searchPlaceholder: string
	emptyLabel: string
	id?: string
	hasError?: boolean
	disabled?: boolean
	/** Show a spinner in place of the chevron while options load (implies disabled). */
	loading?: boolean
	/** Where to portal the option list. Defaults to the body. */
	popupContainer?: RefObject<HTMLElement | null>
}

/**
 * A single-select combobox wired to a string value. Options are `{ value, label }`
 * objects so base-ui filters and displays them automatically. Used for
 * market/city and the CTA pickers.
 */
export const ComboboxField = forwardRef<HTMLDivElement, ComboboxFieldProps>(
	function ComboboxField(
		{
			options,
			value,
			onChange,
			placeholder,
			searchPlaceholder,
			emptyLabel,
			id,
			hasError,
			disabled,
			loading,
			popupContainer
		},
		ref
	) {
		const selectedOption = useMemo(
			() => options.find((option) => option.value === value) ?? null,
			[options, value]
		)
		const isDisabled = disabled || loading

		return (
			<Combobox
				disabled={isDisabled}
				items={options}
				onValueChange={(next) => onChange(next ? next.value : null)}
				value={selectedOption}
			>
				<ComboboxTrigger
					disabled={isDisabled}
					hasError={hasError}
					id={id}
					loading={loading}
					ref={ref}
				>
					<span
						className={cn(
							'truncate',
							selectedOption
								? 'text-neutral-900'
								: 'text-neutral-400'
						)}
					>
						{selectedOption ? selectedOption.label : placeholder}
					</span>
				</ComboboxTrigger>
				<ComboboxContent
					className='w-(--anchor-width)'
					container={popupContainer}
				>
					<ComboboxInput placeholder={searchPlaceholder} />
					<ComboboxList>
						{(item: ComboboxOption) => (
							<ComboboxItem
								className='whitespace-nowrap'
								key={item.value}
								value={item}
							>
								{item.label}
							</ComboboxItem>
						)}
					</ComboboxList>
					<ComboboxEmpty>{emptyLabel}</ComboboxEmpty>
				</ComboboxContent>
			</Combobox>
		)
	}
)

ComboboxField.displayName = 'ComboboxField'
